import {Button, Switch, Table, TableRow, TextInput} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import type {OfferedTrimResponseBody} from '@dms/api/new-vehicle';

import {isNotNilOrEmpty, type Nullish} from 'shared/web';

import {Cell, column, handleCatalogueError, t} from '../catalogueUi';
import {firstIssue, optionalMoneyInput, optionalPriceInput} from '../moneyInput';
import {
  fillOfferedRows,
  startPriceOf,
  trimPriceRequired,
  type OfferedTrimRow,
} from '../utils/offeredTrimRows';
import {GrossPriceText} from './GrossPriceText';
import {SamePriceDialog} from './SamePriceDialog';

const priceErrorOf = (row: OfferedTrimRow) =>
  row.priceNet === '' ? trimPriceRequired() : firstIssue(optionalPriceInput, row.priceNet);

type OfferedTrimsTableProps = {
  rows: OfferedTrimRow[];
  /** A stored trim switched off and on again gets its stored price and cost back. */
  stored: OfferedTrimResponseBody[];
  onChange: (rows: OfferedTrimRow[]) => void;
  trims: {id: string; name: string}[];
  itemName: string;
  currency: string;
  vatRate: string | Nullish;
  canViewCost: boolean;
  /**
   * Where a ticked trim without a stored row starts when nothing was typed on it; else the item's
   * price on another trim, or empty.
   */
  newRowPrice?: {priceNet: string; costNet: string};
  error?: string;
  /**
   * A published year sets one price on all trims through a new pricelist version; resolves true
   * then, and calls `onCreated` once the version exists.
   */
  onSamePrice?: (priceNet: string, onCreated: () => void) => Promise<boolean>;
  /** While the form saves: the save sends the rows it started with, so nothing here may change. */
  isDisabled?: boolean;
};

/**
 * Where a colour or package is offered and at what price on each trim: the trim / offered / price /
 * cost table of the Colours tab and the package editor. 0 means included on that trim.
 */
export function OfferedTrimsTable(props: OfferedTrimsTableProps) {
  const [isFilling, setFilling] = useState(false);
  const setRow = (trimId: string, change: Partial<OfferedTrimRow>) =>
    props.onChange(
      props.rows.some((row) => row.trimId === trimId)
        ? props.rows.map((row) => (row.trimId === trimId ? {...row, ...change} : row))
        : [...props.rows, {trimId, isOffered: false, priceNet: '', costNet: '', ...change}]
    );
  const setOffered = (trimId: string, isOffered: boolean) => {
    const storedRow = props.stored.find((row) => row.trimId === trimId);
    const keptRow = props.rows.find((row) => row.trimId === trimId);
    setRow(
      trimId,
      match({isOffered, storedRow})
        .returnType<Partial<OfferedTrimRow>>()
        .with({isOffered: false}, () => ({isOffered}))
        .with({storedRow: Pattern.nonNullable}, ({storedRow: stored}) => ({
          isOffered,
          priceNet: stored.priceNet,
          costNet: stored.costNet ?? '',
        }))
        .otherwise(() => ({
          isOffered,
          priceNet: isNotNilOrEmpty(keptRow?.priceNet)
            ? keptRow.priceNet
            : (props.newRowPrice?.priceNet ?? startPriceOf(props.rows)),
          costNet: isNotNilOrEmpty(keptRow?.costNet)
            ? keptRow.costNet
            : (props.newRowPrice?.costNet ?? ''),
        }))
    );
  };
  const fillRows = (priceNet: string) => props.onChange(fillOfferedRows(props.rows, priceNet));
  const fill = (priceNet: string) =>
    (props.onSamePrice?.(priceNet, () => fillRows(priceNet)) ?? Promise.resolve(false))
      .then((isRouted) => {
        setFilling(false);
        if (!isRouted) {
          fillRows(priceNet);
        }
      })
      .catch(handleCatalogueError);

  return (
    <VStack spacing={2}>
      <Table
        columns={[
          column(t('entity.newVehicle.catalogue.trimLevel', 'Trim level')),
          column(t('entity.newVehicle.catalogue.priceNet', 'Price net'), {width: '34%'}),
          ...(props.canViewCost
            ? [column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {width: '28%'})]
            : []),
        ]}
      >
        {props.trims.map((trim) => {
          const row = props.rows.find(({trimId}) => trimId === trim.id);
          return (
            <TableRow key={trim.id}>
              <Cell>
                <Switch
                  label={trim.name}
                  value={row?.isOffered ?? false}
                  isDisabled={props.isDisabled}
                  onChange={(isOffered) => setOffered(trim.id, isOffered)}
                />
              </Cell>
              <Cell>
                {row?.isOffered ? (
                  <VStack spacing={1}>
                    <TextInput
                      aria-label={`${trim.name}, ${t('entity.newVehicle.catalogue.priceNet', 'Price net')}`}
                      value={row.priceNet}
                      isInvalid={isNotNilOrEmpty(priceErrorOf(row))}
                      errorMessage={priceErrorOf(row)}
                      isDisabled={props.isDisabled}
                      onChange={(priceNet) => setRow(trim.id, {priceNet: priceNet ?? ''})}
                    />
                    <GrossPriceText
                      net={row.priceNet}
                      currency={props.currency}
                      vatRate={props.vatRate}
                    />
                  </VStack>
                ) : (
                  <Text size="small" color="tertiary">
                    {t('entity.newVehicle.catalogue.notOffered', 'Not offered')}
                  </Text>
                )}
              </Cell>
              {/* The row maps every child to a column, so the cost cell is left out, not hidden. */}
              {(props.canViewCost ? [row] : []).map((costRow) => (
                <Cell key="cost">
                  <Show when={costRow?.isOffered ? costRow : null}>
                    {(offered) => (
                      <TextInput
                        aria-label={`${trim.name}, ${t('entity.newVehicle.catalogue.costNet', 'Cost net')}`}
                        value={offered.costNet}
                        errorMessage={firstIssue(optionalMoneyInput, offered.costNet)}
                        isInvalid={isNotNilOrEmpty(firstIssue(optionalMoneyInput, offered.costNet))}
                        isDisabled={props.isDisabled}
                        onChange={(costNet) => setRow(trim.id, {costNet: costNet ?? ''})}
                      />
                    )}
                  </Show>
                </Cell>
              ))}
            </TableRow>
          );
        })}
      </Table>
      <Show when={isNotNilOrEmpty(props.error)}>
        <Text size="small" color="danger">
          {props.error}
        </Text>
      </Show>
      <Box>
        <Button
          variant="link"
          size="small"
          title={t('entity.newVehicle.catalogue.samePriceOnAllTrims', 'Same price on all trims')}
          isDisabled={props.isDisabled || !props.rows.some(({isOffered}) => isOffered)}
          onClick={() => setFilling(true)}
        />
      </Box>
      <SamePriceDialog
        itemName={isFilling ? props.itemName : null}
        hint={t(
          'entity.newVehicle.catalogue.samePriceOnOfferedTrimsHint',
          'Sets this price on every trim it is offered on. Trims where it is included (0) keep 0; change those on their own.'
        )}
        startPrice={startPriceOf(props.rows)}
        currency={props.currency}
        vatRate={props.vatRate}
        onApply={fill}
        onClose={() => setFilling(false)}
      />
    </VStack>
  );
}
